import { asArray } from "@/lib/asArray";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, Upload, FileArchive,
  Copy, CheckCircle2, XCircle, Info,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTimeFirm } from "@/lib/dt";

// Render a monospace hash + a copy button. Kept dumb; no side effects.
function HashRow({ label, value, testid }) {
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success("Hash copied"),
      () => toast.error("Copy failed"),
    );
  };
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border bg-slate-50 p-3 dark:bg-slate-900/50">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-0.5 break-all font-mono text-[11px] leading-relaxed" data-testid={testid}>
          {value || <span className="text-muted-foreground">— not present —</span>}
        </div>
      </div>
      {value && (
        <button
          type="button"
          onClick={copy}
          className="mt-4 inline-flex h-6 w-6 flex-none items-center justify-center rounded border text-muted-foreground hover:bg-white hover:text-foreground dark:hover:bg-slate-800"
          data-testid={`${testid}-copy`}
          title="Copy hash"
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function CheckPill({ ok, label, testid }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
        ok
          ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
          : "border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
      }`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      <span className="font-medium">{label}</span>
    </div>
  );
}

export default function BundleVerifier() {
  const { t, lang } = useI18n();
  const fmtDate = (iso) => (iso ? fmtDateTimeFirm(iso).slice(0, 11) : "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload the .zip signed bundle");
      e.target.value = "";
      return;
    }
    setFile(f);
    setResult(null);
  };

  const verify = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/signoff/verify-bundle", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        _silent: true,
      });
      setResult(r.data);
      const v = r.data?.verdict;
      if (v === "PASS") toast.success("Bundle integrity verified");
      else if (v === "FAIL") toast.error("Bundle integrity check failed");
      else if (v === "NOT_ON_RECORD") toast.message(t("sdg4.bv.notOnRecordTitle"));   // Silent defaults · A-2
      else toast.message("Foreign bundle — hash computed, no on-record match");
    } catch (err) {
      toast.error(toErrMsg(err, "Verification failed"));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); };

  const verdict = result?.verdict;
  const VerdictIcon =
    verdict === "PASS" ? ShieldCheck :
    verdict === "FAIL" ? ShieldAlert :
    ShieldQuestion;
  const verdictClass =
    verdict === "PASS" ? "bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800" :
    verdict === "FAIL" ? "bg-red-50 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800" :
    "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800";
  const verdictBlurb =
    verdict === "PASS" ? "The bundle's snapshot hash matches the value stamped inside the archive" +
                          (result?.on_record_hash ? " and the value on record with Azex." : ".") :
    verdict === "FAIL" ? "One or more hashes do not match — the bundle contents may have been altered since sign-off." :
    verdict === "UNKNOWN" ? "This bundle was not issued by this Azex instance, so we cannot cross-reference the hash. The recomputed SHA-256 is shown below for your own reference." :
    verdict === "NOT_ON_RECORD" ? ((lang === "ar" && result?.verdict_message_ar) || result?.verdict_message || t("sdg4.bv.notOnRecordBlurb")) :   // Silent defaults · A-2
    "";

  return (
    <div className="space-y-6" data-testid="bundle-verifier-page">
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">{t("b3.bv.title")}</h1>
        <p className="text-sm text-muted-foreground">
          Upload any Azex-issued signed engagement bundle (<code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] dark:bg-slate-800">.zip</code>) and we
          re-compute the SHA-256 over its <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] dark:bg-slate-800">snapshot.json</code> and cross-check
          it against the manifest inside the archive and the record on file. Read-only — nothing about the engagement changes.
        </p>
        <div className="flex gap-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200"
             data-testid="bundle-verifier-disclosure">
          <Info className="mt-0.5 h-4 w-4 flex-none" />
          <div>
            <span className="font-semibold">{t("b3.bv.scope")}</span> this is a convenient in-app confirmation that the file matches the record Azex holds on file for this engagement.
            {" "}For situations that call for fully independent verification (e.g. a formal dispute, or a party who cannot rely on Azex itself),
            re-compute the same SHA-256 outside Azex — any standard hashing tool works on the <code className="rounded bg-white/70 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-900/60">snapshot.json</code> inside
            the ZIP — and compare it against the hash captured in the engagement's{" "}
            <Link to="/audit-trail?action=signed_off"
                  className="font-semibold underline decoration-dotted underline-offset-2 hover:text-sky-700 dark:hover:text-sky-100"
                  data-testid="bundle-verifier-audit-trail-link">Audit Trail</Link>
            {" "}at the moment of sign-off (event: <code className="rounded bg-white/70 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-900/60">signed_off</code>, field <code className="rounded bg-white/70 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-900/60">details.hash</code>).
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("b3.bv.upload")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-8 text-sm hover:border-emerald-500 hover:bg-emerald-50/40 dark:border-slate-700">
            <Upload className="h-5 w-5" />
            <span>
              {file ? (
                <span className="inline-flex items-center gap-2">
                  <FileArchive className="h-4 w-4" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-muted-foreground">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                </span>
              ) : "Choose a signed bundle .zip"}
            </span>
            <input type="file" accept=".zip" className="hidden" onChange={pick}
                   data-testid="bundle-verifier-file-input" disabled={busy} />
          </label>
          <div className="flex items-center gap-2">
            <Button onClick={verify} disabled={!file || busy} data-testid="bundle-verifier-verify-btn">
              {busy ? "Verifying…" : "Verify integrity"}
            </Button>
            {(file || result) && (
              <Button variant="ghost" size="sm" onClick={reset} data-testid="bundle-verifier-reset-btn">
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <>
          {result.audit_trail_changed_after_signing && (
            <Card className="border border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
                  data-testid="bundle-verifier-audit-trail-changed">
              <CardContent className="flex items-start gap-3 p-4">
                <ShieldAlert className="mt-0.5 h-6 w-6 flex-none" />
                <div>
                  <div className="text-sm font-semibold" data-testid="bundle-verifier-audit-trail-changed-title">
                    Audit trail changed after signing
                  </div>
                  <div className="mt-1 text-xs" data-testid="bundle-verifier-audit-trail-changed-detail">
                    {result.covered_deletion_count || 0} audit-trail entry(ies) covered by this signed bundle were deleted after the sign-off date.
                  </div>
                  <div dir="rtl" className="mt-1 text-xs" data-testid="bundle-verifier-audit-trail-changed-ar">
                    تغيّر سجل التدقيق بعد التوقيع — تم حذف {result.covered_deletion_count || 0} من قيود سجل التدقيق التي يغطيها هذا الملف الموقَّع بعد تاريخ التوقيع.
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className={`border ${verdictClass}`} data-testid="bundle-verifier-verdict-card">
            <CardContent className="flex items-start gap-4 p-5">
              <VerdictIcon className="mt-0.5 h-8 w-8 flex-none" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold" data-testid="bundle-verifier-verdict">
                    {verdict === "PASS" ? "PASS — bundle intact" :
                     verdict === "FAIL" ? "FAIL — hash mismatch" :
                     verdict === "NOT_ON_RECORD" ? t("sdg4.bv.notOnRecordTitle") :   // Silent defaults · A-2 (amber)
                     "UNKNOWN — foreign bundle"}
                  </span>
                </div>
                <p className="mt-1 text-sm">{verdictBlurb}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <CheckPill
                    ok={!!result.match_manifest}
                    label={result.manifest_hash
                      ? (result.match_manifest ? "Matches manifest (signature_hash.txt)" : "Does not match manifest")
                      : "No manifest present in archive"}
                    testid="check-manifest"
                  />
                  <CheckPill
                    ok={!!result.match_on_record}
                    label={result.on_record_hash
                      ? (result.match_on_record ? "Matches Azex record" : "Does not match Azex record")
                      : "No Azex record for this engagement"}
                    testid="check-onrecord"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">SHA-256 hashes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <HashRow label="Re-computed (this session)" value={result.computed_hash} testid="hash-computed" />
              <HashRow label="Manifest (from signature_hash.txt inside the ZIP)" value={result.manifest_hash} testid="hash-manifest" />
              <HashRow label="On record (Azex signoff_bundles)" value={result.on_record_hash} testid="hash-onrecord" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("b3.bv.snapshot")}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">{t("b3.bv.firm")}</span><div className="font-medium">{result.firm?.name || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.client")}</span><div className="font-medium">{result.engagement?.client_name || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.engRef")}</span><div className="font-medium">{result.engagement?.firm_engagement_ref || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.fy")}</span><div className="font-medium">FY {result.engagement?.financial_year || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.periodEnd")}</span><div className="font-medium">{fmtDate(result.engagement?.period_end) || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.currency")}</span><div className="font-medium">{result.engagement?.currency || "—"}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.signedBy")}</span><div className="font-medium">{result.signed_off_by?.name || "—"} {result.signed_off_by?.role ? <span className="text-muted-foreground">({result.signed_off_by.role})</span> : null}</div></div>
              <div><span className="text-muted-foreground">{t("b3.bv.signedAt")}</span><div className="font-medium tabular-nums">{fmtDateTimeFirm(result.signed_off_at) || "—"}</div></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("b3.bv.fingerprint")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{t("b3.bv.samples")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.samples ?? 0}</span></Badge>
                <Badge variant="outline">{t("b3.bv.vouching")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.vouching_items ?? 0}</span></Badge>
                <Badge variant="outline">{t("b3.bv.documents")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.documents ?? 0}</span></Badge>
                <Badge variant="outline">{t("b3.bv.discrepancies")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.discrepancies ?? 0}</span></Badge>
                <Badge variant="outline">{t("b3.bv.riskItems")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.risk_items ?? 0}</span></Badge>
                <Badge variant="outline">{t("b3.bv.attachments")}: <span className="ms-1 tabular-nums font-semibold">{result.counts?.attachments ?? 0}</span></Badge>
              </div>
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("b3.bv.files")}</div>
                <ul className="rounded-md border bg-slate-50 p-3 font-mono text-[11px] leading-6 dark:bg-slate-900/50" data-testid="bundle-file-list">
                  {asArray(result.bundle_contents).map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
